import { PropsWithChildren } from 'react';

import {
  LabelChip,
  ExternalLink,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';

import { getDocsLink, DocsLinkKey } from '@cast/utils';

export type SettingsFormProps = PropsWithChildren<{
  title: string;
  description?: React.ReactNode;
  titleAdornment?: React.ReactNode;
  topContent?: React.ReactNode;
  bottomContent?: React.ReactNode;
  docsLinkKey?: DocsLinkKey;
  testId?: string;
  contentWidth?: number;
  isDefault?: boolean;
  isNew?: boolean;
}>;

export const SettingsForm = ({
  title,
  description,
  children,
  titleAdornment,
  docsLinkKey,
  topContent,
  bottomContent,
  testId = 'settings-form',
  contentWidth = 560,
  isDefault,
  isNew,
}: SettingsFormProps) => {
  return (
    <Stack gap={24} data-testid={testId}>
      {topContent}
      <Stack gap={8}>
        <Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" gap={8} alignItems="flex-end">
              <Typography variant="h5" data-testid="title">
                {title}
              </Typography>
              {isNew && (
                <LabelChip color="indigo" size="sm">
                  NEW
                </LabelChip>
              )}
              {titleAdornment}
              {isDefault && (
                <Typography
                  variant="P12R"
                  sx={{ color: tokens.theme.fg.structural.secondary }}
                  data-testid="default-label"
                  pb={2}
                >
                  (default)
                </Typography>
              )}
            </Stack>

            {docsLinkKey && (
              <ExternalLink
                link={getDocsLink(docsLinkKey)}
                sx={{
                  typography: 'P12M',
                  color: tokens.theme.fg.selection.primary,
                }}
              >
                Documentation
              </ExternalLink>
            )}
          </Stack>

          {description && (
            <Typography
              variant="P12R"
              sx={{ color: tokens.theme.fg.structural.secondary }}
              data-testid="description"
              maxWidth={560}
            >
              {description}
            </Typography>
          )}
        </Stack>
      </Stack>
      <Stack gap={24} maxWidth={contentWidth}>
        {children}
      </Stack>
      {bottomContent}
    </Stack>
  );
};
